import { createFileRoute, Link } from '@tanstack/react-router';
import { format, parseISO } from 'date-fns';
import { ExternalLink } from 'lucide-react';
import { Fragment, useMemo } from 'react';
import {
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { z } from 'zod';

import { ProductQualityDashboard } from '@apps/web/components/admin/product_quality/dashboard';
import { Badge } from '@apps/web/components/ui/badge';
import { DataTable } from '@apps/web/components/ui/data_table';
import { Skeleton } from '@apps/web/components/ui/skeleton';
import { AdminErrorState } from '@apps/web/features/admin_design/admin_states';
import { formatDateAndTimeWithTz } from '@apps/web/lib/date_utils';
import { type ColumnDef } from '@apps/web/lib/table';
import {
    EvidenceFamilyBadge,
    formatPercent,
} from '@apps/web/routes/_app/admin/voice-evals/shared';
import { formatBucketSlug } from '@apps/web/routes/_app/admin/voice-qa/bucket_formatting';
import { useAdminQualityScoreboard } from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { type ApiResponseType } from '@libraries/typescript/api_client';

const qualityScoreboardSearchSchema = z.object({
    days: z.union([z.literal(7), z.literal(28), z.literal(90)]).default(7),
    organizationId: z.string().optional(),
    view: z
        .enum(['overview', 'problems', 'diagnostics', 'reports'])
        .default('overview'),
    problemId: z.string().optional(),
    snapshotId: z.string().optional(),
    painPoint: z.string().optional(),
    severity: z.enum(['critical', 'material', 'minor']).optional(),
    owner: z.string().optional(),
    integration: z.string().optional(),
});

export const Route = createFileRoute('/_app/admin/quality-scoreboard')({
    head: () => ({ meta: [{ title: 'Product Quality | Phoebe Admin' }] }),
    component: ProductQualityPage,
    validateSearch: qualityScoreboardSearchSchema,
});

function ProductQualityPage() {
    const filters = Route.useSearch();
    const navigate = Route.useNavigate();
    return (
        <ProductQualityDashboard
            filters={filters}
            onChange={(patch) => {
                void navigate({
                    search: (current) => ({ ...current, ...patch }),
                });
            }}
            diagnostics={
                <QualityDiagnostics
                    days={filters.days}
                    organizationId={filters.organizationId}
                />
            }
        />
    );
}

type QualityScoreboardResponse = ApiResponseType<
    '/admin/quality-scoreboard',
    'get'
>;
type QualityTopic = QualityScoreboardResponse['topics'][number];

function scoreLabel(topic: QualityTopic): string {
    if (topic.scored_run_count === 0) {
        return 'No scored runs';
    }
    return `${topic.bad_count} / ${topic.scored_run_count} scored · ${formatPercent(
        topic.bad_rate,
    )}`;
}

function TrendChart({ topic }: { readonly topic: QualityTopic }) {
    const points = topic.daily.map((day) => ({
        metricDateLocal: day.metric_date_local,
        badRate: day.bad_rate === null ? null : day.bad_rate * 100,
        runs: day.run_count,
        scoredRuns: day.scored_run_count,
    }));
    const accessibleSummary = points
        .map((point) => {
            const dateLabel = format(parseISO(point.metricDateLocal), 'MMM d');
            if (point.scoredRuns === 0) {
                return `${dateLabel}: no scored runs`;
            }
            return `${dateLabel}: ${formatPercent(
                point.badRate === null ? null : point.badRate / 100,
            )} BAD across ${point.scoredRuns} scored runs`;
        })
        .join('; ');
    return (
        <div
            className="h-14 min-w-32"
            role="img"
            tabIndex={0}
            aria-label={`Daily BAD rate for ${topic.topic_label}, ${topic.channel}. ${accessibleSummary}`}
        >
            <ResponsiveContainer width="100%" height="100%" aria-hidden={true}>
                <LineChart data={points} accessibilityLayer={false}>
                    <XAxis dataKey="metricDateLocal" hide={true} />
                    <YAxis domain={[0, 100]} hide={true} />
                    <RechartsTooltip
                        content={({ active, payload }) => {
                            const point = payload?.[0]?.payload as
                                | (typeof points)[number]
                                | undefined;
                            if (!active || !point) {
                                return null;
                            }
                            return (
                                <div className="bg-popover text-popover-foreground rounded-md border p-2 text-xs shadow-md">
                                    <div className="font-medium">
                                        {format(
                                            parseISO(point.metricDateLocal),
                                            'MMM d',
                                        )}
                                    </div>
                                    <div className="text-muted-foreground">
                                        {point.scoredRuns === 0
                                            ? 'No scored runs'
                                            : `${formatPercent(
                                                  point.badRate === null
                                                      ? null
                                                      : point.badRate / 100,
                                              )} BAD · ${point.scoredRuns} scored`}
                                    </div>
                                </div>
                            );
                        }}
                    />
                    <Line
                        dataKey="badRate"
                        stroke="var(--destructive)"
                        strokeWidth={2}
                        dot={false}
                        connectNulls={false}
                        isAnimationActive={false}
                    />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
}

function TopicKindBadge({ topic }: { readonly topic: QualityTopic }) {
    switch (topic.topic_kind) {
        case 'inferred_or_workflow_derived':
            return <Badge variant="secondary">Inferred / workflow</Badge>;
        case 'unclassified_engaged':
            return <Badge variant="outline">No topic</Badge>;
        case 'no_conversation_topic':
            return <Badge variant="outline">No conversation topic</Badge>;
        default:
            return null;
    }
}

function RelatedEvals({
    evals,
}: {
    readonly evals: QualityTopic['related_evals'];
}) {
    if (evals.length === 0) {
        return (
            <span className="text-muted-foreground text-sm">
                No related eval
            </span>
        );
    }
    if (evals.every((evidence) => !evidence.latest)) {
        return (
            <span className="text-muted-foreground text-sm">
                No complete daily eval in 90 days
            </span>
        );
    }
    return (
        <div className="flex min-w-56 flex-col gap-1.5">
            {evals.map((evidence) => (
                <div key={evidence.use_case} className="flex flex-col gap-0.5">
                    {evidence.latest ? (
                        <Fragment>
                            <div className="flex flex-wrap items-center gap-1.5">
                                <Link
                                    to="/admin/voice-evals/runs/$runId"
                                    params={{ runId: evidence.latest.run_id }}
                                    className="inline-flex items-center gap-1 text-sm underline-offset-4 hover:underline"
                                >
                                    {evidence.use_case.replace(/_/g, ' ')}
                                    <ExternalLink className="h-3 w-3" />
                                </Link>
                                <span className="text-sm">
                                    {evidence.latest.passed_count} /{' '}
                                    {evidence.latest.attempted_count} ·{' '}
                                    {formatPercent(evidence.latest.pass_rate)}
                                </span>
                                <EvidenceFamilyBadge
                                    family={evidence.latest.family}
                                    className="text-xs"
                                />
                            </div>
                            <div className="text-muted-foreground text-xs">
                                CI daily ·{' '}
                                {formatDateAndTimeWithTz(
                                    evidence.latest.finished_at,
                                    'UTC',
                                )}
                            </div>
                            <div className="text-muted-foreground text-xs">
                                {evidence.trailing_14d
                                    ? `Comparable 14-day evidence: ${evidence.trailing_14d.passed_count} / ${evidence.trailing_14d.attempted_count} across ${evidence.trailing_14d.run_count} runs · ${formatPercent(evidence.trailing_14d.pass_rate)}`
                                    : 'No comparable 14-day evidence'}
                            </div>
                        </Fragment>
                    ) : (
                        <Fragment>
                            <span className="text-sm">
                                {evidence.use_case.replace(/_/g, ' ')}
                            </span>
                            <span className="text-muted-foreground text-xs">
                                No complete daily eval in 90 days
                            </span>
                        </Fragment>
                    )}
                </div>
            ))}
        </div>
    );
}

function ScoreboardSkeleton() {
    return (
        <div className="space-y-3" role="status">
            <span className="sr-only">Loading…</span>
            {Array.from({ length: 6 }, (_, index) => (
                <Skeleton key={index} className="h-16 w-full" />
            ))}
        </div>
    );
}

function QualityDiagnostics({
    days,
    organizationId,
}: {
    readonly days: 7 | 28 | 90;
    readonly organizationId: string | undefined;
}) {
    const { data, error, isLoading, mutate } = useAdminQualityScoreboard({
        days,
        organization_id: organizationId,
    });
    const columns = useMemo<Array<ColumnDef<QualityTopic>>>(
        () => [
            {
                id: 'topic',
                header: 'Topic',
                cell: ({ row }) => (
                    <div className="flex min-w-48 flex-col gap-1">
                        <span className="font-medium">
                            {row.original.topic_label}
                        </span>
                        <TopicKindBadge topic={row.original} />
                    </div>
                ),
            },
            {
                accessorKey: 'channel',
                header: 'Channel',
                cell: ({ row }) => (
                    <span className="capitalize">{row.original.channel}</span>
                ),
            },
            {
                id: 'bad-rate',
                header: 'BAD rate',
                cell: ({ row }) => (
                    <div className="min-w-36">
                        <div className="font-medium">
                            {scoreLabel(row.original)}
                        </div>
                        <div className="text-muted-foreground text-xs">
                            {row.original.run_count} total ·{' '}
                            {row.original.unscored_run_count} unscored
                        </div>
                    </div>
                ),
            },
            {
                id: 'trend',
                header: 'Daily BAD rate',
                cell: ({ row }) => <TrendChart topic={row.original} />,
            },
            {
                id: 'issues',
                header: 'BAD runs with issue',
                cell: ({ row }) => {
                    const issues = row.original.top_issues;
                    if (issues.length === 0) {
                        return (
                            <span className="text-muted-foreground text-sm">
                                —
                            </span>
                        );
                    }
                    return (
                        <div className="flex min-w-48 flex-col gap-1 text-sm">
                            {issues.slice(0, 3).map((issue) => (
                                <span
                                    key={`${issue.issue_bucket}:${issue.issue_secondary_bucket}`}
                                >
                                    {issue.bad_run_count} ·{' '}
                                    {formatBucketSlug(issue.issue_bucket)}
                                    {issue.issue_secondary_bucket === 'none'
                                        ? null
                                        : ` / ${formatBucketSlug(
                                              issue.issue_secondary_bucket,
                                          )}`}
                                </span>
                            ))}
                        </div>
                    );
                },
            },
            {
                id: 'related-evals',
                header: 'Related evals',
                cell: ({ row }) => (
                    <RelatedEvals evals={row.original.related_evals} />
                ),
            },
        ],
        [],
    );

    return (
        <section
            aria-label="Automated quality reviews"
            className="flex h-full flex-col overflow-auto"
        >
            <div className="border-border border-b px-6 py-4">
                <h2 className="text-lg font-semibold">
                    Automated quality reviews
                </h2>
                <p className="text-muted-foreground mt-1 max-w-3xl text-sm">
                    Topic labels come from the reviewer or an exact workflow
                    fallback. They are not direct user-selected labels.
                </p>
            </div>
            <div className="p-6">
                {data && !error ? (
                    <div className="text-muted-foreground mb-4 flex flex-wrap gap-x-4 gap-y-1 rounded-md border px-3 py-2 text-xs">
                        <span>
                            {`Source: ${
                                data.quality_data_source ===
                                'production_read_replica'
                                    ? 'Live production read replica'
                                    : 'Live application database'
                            }`}
                        </span>
                        <span>
                            Data as of{' '}
                            <time dateTime={data.quality_source_as_of_utc}>
                                {formatDateAndTimeWithTz(
                                    data.quality_source_as_of_utc,
                                    'UTC',
                                )}
                            </time>
                        </span>
                        {data.quality_replica_lag_seconds === null ? null : (
                            <span>
                                {`Replica lag: ${Math.round(
                                    data.quality_replica_lag_seconds,
                                )}s`}
                            </span>
                        )}
                        <span>Complete organization-local days only</span>
                    </div>
                ) : null}
                {error ? (
                    <AdminErrorState
                        title="Failed to load the quality scoreboard"
                        description="The read-only quality source is temporarily unavailable. Retry in one minute."
                        onRetry={() => {
                            void mutate();
                        }}
                    />
                ) : isLoading ? (
                    <ScoreboardSkeleton />
                ) : !data?.has_observations ? (
                    <div className="text-muted-foreground rounded-md border px-6 py-16 text-center text-sm">
                        No quality runs in this window. The scoreboard shows
                        live SMS inline reviews and post-call voice audits. It
                        does not treat missing judgments as good.
                    </div>
                ) : (
                    <DataTable
                        columns={columns}
                        data={data.topics}
                        hidePagination={true}
                        tableRowsClassName="align-top"
                    />
                )}
            </div>
        </section>
    );
}
